import { useEffect, useMemo, useState } from "react";
import { useForm, useNotification } from "@/hooks";
import { Content } from "@/layout";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    Heading,
    LoadingProgress,
    MessageBanner,
    Panel,
    Select,
    Table,
    TableFooter,
    TableToolbar,
    Text,
} from "@/ui";
import { TreezObjectModal } from "./components";
import { useStoreContext } from "context";
import { DiscountsProvider } from "data/sell-treez/context";
import { useDiscounts } from "../../data/sell-treez/hook/useDiscounts";
import { NOT_AVAILABLE } from "@/config";
import { stringToSlug } from "utils/utils";
import styles from "./TreezDiscounts.module.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const mapSpecialToRow = (raw, index) => {
    const id =
        raw?.discount_id ??
        raw?.id ??
        raw?.discount_id_string ??
        `discount-${index}`;
    return {
        id,
        title: raw?.discount_title ?? raw?.title ?? NOT_AVAILABLE,
        method: raw?.method ?? NOT_AVAILABLE,
        active: Boolean(raw?.active),
        amount: raw?.amount ?? 0,
        _raw: raw,
    };
};

const TreezDiscountLoaded = ({
    entityId,
    inputs,
    setInputs,
    selectChange,
    providers,
    loadingApps,
    treezSellConfigsLoading,
}) => {
    const notify = useNotification();
    const {
        discounts,
        isLoading: discountsLoading,
        isFetching: discountsFetching,
        isError,
        error,
    } = useDiscounts();

    const [selectedDiscount, setSelectedDiscount] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [methodFilter, setMethodFilter] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const storeOptions = useMemo(
        () =>
            (providers || []).map(provider => ({
                value: String(provider.value),
                label: provider.label,
            })),
        [providers],
    );

    const rows = useMemo(
        () => (discounts || []).map((raw, idx) => mapSpecialToRow(raw, idx)),
        [discounts],
    );

    const methodOptions = useMemo(() => {
        const methods = new Set();
        rows.forEach(row => {
            const method = String(row.method ?? "").trim();
            if (method && method !== NOT_AVAILABLE) {
                methods.add(method);
            }
        });
        return Array.from(methods).sort((a, b) => a.localeCompare(b));
    }, [rows]);

    const methodMenuItems = useMemo(
        () => [
            {
                id: "all-methods",
                label: "All methods",
                onSelect: () => {
                    setMethodFilter(null);
                    setPage(1);
                },
            },
            ...methodOptions.map(method => ({
                id: `method-${method}`,
                label: method,
                onSelect: () => {
                    setMethodFilter(method);
                    setPage(1);
                },
            })),
        ],
        [methodOptions],
    );

    const toolbarFilters = useMemo(
        () => [
            {
                id: "f-method",
                label: methodFilter ? `Method · ${methodFilter}` : "Method",
                active: Boolean(methodFilter),
                menuItems: methodFilter ? undefined : methodMenuItems,
                onRemove: methodFilter
                    ? () => {
                          setMethodFilter(null);
                          setPage(1);
                      }
                    : undefined,
            },
        ],
        [methodFilter, methodMenuItems],
    );

    useEffect(() => {
        if (!providers?.length || inputs.store) {
            return;
        }

        setInputs(prev => ({
            ...prev,
            store: providers[0]?.value || null,
        }));
    }, [providers, inputs.store, setInputs]);

    useEffect(() => {
        setMethodFilter(null);
        setPage(1);
    }, [inputs.store]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, methodFilter]);

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();

        return rows.filter(item => {
            if (methodFilter && String(item.method) !== methodFilter) {
                return false;
            }
            if (!q) {
                return true;
            }
            const title = String(item.title ?? "").toLowerCase();
            const method = String(item.method ?? "").toLowerCase();
            return title.includes(q) || method.includes(q);
        });
    }, [rows, searchQuery, methodFilter]);

    const hasActiveFilters =
        Boolean(searchQuery.trim()) || Boolean(methodFilter);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * pageSize;
    const pageRows = useMemo(
        () => filtered.slice(pageStart, pageStart + pageSize),
        [filtered, pageStart, pageSize],
    );

    const clipboardCopy = item => {
        const discountUrl = `/special/${stringToSlug(item?.title)}?special_id=${
            item?.id
        }`;
        navigator.clipboard.writeText(discountUrl);
        notify(`Discount URL: ${discountUrl} is copied`, "success");
    };

    const columns = useMemo(
        () => [
            {
                id: "title",
                header: "Title",
                width: "40%",
                minWidth: 220,
                renderCell: row => (
                    <div className={styles.primaryCell}>
                        {row.title || NOT_AVAILABLE}
                    </div>
                ),
            },
            {
                id: "method",
                header: "Method",
                width: "20%",
                minWidth: 140,
                renderCell: row => (
                    <div className={styles.secondaryCell}>
                        {row.method || NOT_AVAILABLE}
                    </div>
                ),
            },
            {
                id: "active",
                header: "Active",
                width: 100,
                minWidth: 90,
                renderCell: row => (
                    <Badge variant={row.active ? "primary" : "neutral"}>
                        {row.active ? "Yes" : "No"}
                    </Badge>
                ),
            },
            {
                id: "amount",
                header: "Amount",
                width: 100,
                minWidth: 90,
                renderCell: row => (
                    <div className={styles.primaryCell}>{row.amount || 0}</div>
                ),
            },
            {
                id: "url",
                header: "URL",
                width: 160,
                minWidth: 160,
                renderCell: row => (
                    <span
                        className={styles.copyAction}
                        onClick={event => event.stopPropagation()}
                    >
                        <Button
                            iconRight={<Icon color="#727a94" icon="copy" />}
                            onClick={() => clipboardCopy(row)}
                            size="sm"
                            title="Copy Special Url"
                            type="button"
                            variant="tertiary"
                        >
                            Copy URL
                        </Button>
                    </span>
                ),
            },
        ],
        [],
    );

    const tableLoading =
        loadingApps ||
        treezSellConfigsLoading ||
        discountsLoading ||
        discountsFetching;

    return (
        <>
            <Content>
                <Panel
                    className={styles.root}
                    header={
                        <div className={styles.headerBar}>
                            <div className={styles.headerText}>
                                <Heading level={4}>Discounts</Heading>
                                <Text tone="secondary" variant="body-md">
                                    All Treez Discounts available in the system
                                </Text>
                            </div>
                            <Select
                                className={styles.storeSelect}
                                fullWidth
                                label="Store"
                                onValueChange={value =>
                                    selectChange({ value }, "store")
                                }
                                options={storeOptions}
                                value={
                                    inputs.store
                                        ? String(inputs.store)
                                        : undefined
                                }
                            />
                        </div>
                    }
                    showHeaderDivider
                >
                    {(inputs.store && !entityId && !treezSellConfigsLoading) ||
                    (isError && error) ? (
                        <div className={styles.banners}>
                            {inputs.store &&
                            !entityId &&
                            !treezSellConfigsLoading ? (
                                <MessageBanner
                                    aria-label="Missing entity id"
                                    description={
                                        <>
                                            Sell Treez config has no{" "}
                                            <strong>entity_id</strong> for this
                                            dispensary. OpenSearch discounts
                                            stay disabled until the config
                                            includes an entity id.
                                        </>
                                    }
                                    role="status"
                                />
                            ) : null}
                            {isError && error ? (
                                <MessageBanner
                                    aria-label="Discounts OpenSearch error"
                                    description={
                                        <>
                                            <strong>
                                                Discounts OpenSearch:
                                            </strong>{" "}
                                            {error?.message ?? "Request failed"}
                                        </>
                                    }
                                    role="alert"
                                />
                            ) : null}
                        </div>
                    ) : null}

                    <TableToolbar
                        count={filtered.length}
                        filters={toolbarFilters}
                        label="Discounts"
                        mode={
                            hasActiveFilters ? "active-filters" : "filterable"
                        }
                        onClearAllFilters={() => {
                            setSearchQuery("");
                            setMethodFilter(null);
                            setPage(1);
                        }}
                        onSearchChange={value => {
                            setSearchQuery(value ?? "");
                        }}
                        searchable
                        searchValue={searchQuery}
                        showMoreFilters={false}
                        updatedAtLabel=""
                    />

                    {tableLoading ? (
                        <div className={styles.loading}>
                            <LoadingProgress
                                indeterminate
                                indicator="circular"
                                size="sm"
                                title="Loading"
                            />
                        </div>
                    ) : (
                        <>
                            <Table
                                columns={columns}
                                emptyMessage="No discounts found"
                                getRowId={row => String(row.id)}
                                onRowClick={row => {
                                    setSelectedDiscount(row);
                                    setIsModalOpen(true);
                                }}
                                rows={pageRows}
                            />
                            <TableFooter
                                onPageChange={nextPage => {
                                    setPage(Math.max(1, nextPage));
                                }}
                                onPageSizeChange={size => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                                page={currentPage}
                                pageSize={pageSize}
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                totalPages={totalPages}
                                totalRows={filtered.length}
                            />
                        </>
                    )}
                </Panel>
            </Content>
            <TreezObjectModal
                handleModalToggle={() => setIsModalOpen(false)}
                isModalOpen={isModalOpen}
                object={selectedDiscount}
                title="Discount Details"
            />
        </>
    );
};

const TreezDiscount = () => {
    const {
        providers,
        storeAppsLoading: loadingApps,
        getTreezStoreContextByStoreId,
        store,
        treezSellConfigsLoading,
    } = useStoreContext();

    const { inputs, setInputs, selectChange } = useForm();

    const entityId = useMemo(() => {
        if (!inputs.store) {
            return undefined;
        }
        return getTreezStoreContextByStoreId(inputs.store)?.mso_store_entity_id;
    }, [getTreezStoreContextByStoreId, inputs.store]);

    return (
        <DiscountsProvider
            entityId={entityId}
            storeShortName={inputs.store}
            timezone={store?.timezone || undefined}
        >
            <TreezDiscountLoaded
                entityId={entityId}
                inputs={inputs}
                loadingApps={loadingApps}
                providers={providers}
                selectChange={selectChange}
                setInputs={setInputs}
                treezSellConfigsLoading={treezSellConfigsLoading}
            />
        </DiscountsProvider>
    );
};

export default TreezDiscount;
